import React, { useState, useEffect }  from 'react';
import { browserHistory } from 'react-router';
import { Form, Password, VALIDATION_STATES } from '@parchment/forms';
import { ValidationRequiredIcon, EditIcon } from '@parchment/icons';
import {
    Anchor,
    alto,
    Banner,
    Button,
    FormContainer,
    Heading4 as Heading4Raw,
    Loader,
    thunderBird,
    white
} from '@parchment/atoms';
import styled from 'styled-components';
import { ORDER_TYPE } from '../common/constants/OrderType';
import useAuthenticateUser from '../common/hooks/AuthenticateUserHooks';
import useEmailVerification from '../common/hooks/EmailVerificationHooks';

const noop = () => {};

export default function LoginView ({
    email = '',
    member = {},
    resetEmail = noop,
    orderType = ORDER_TYPE.LEARNER,
    submitting = false
}) {
    const [ password, setPassword ] = useState('');
    const [ passwordValidationState, setPasswordValidationState ] = useState(VALIDATION_STATES.neutral);

    const [ formStatus, setFormStatus ] = useState({
        showErrorBanner: false,
        errorBannerMessage: '',
        submitting: submitting
    });

    const { authenticationInfo, loginUser, status: authenticationStatus } = useAuthenticateUser();
    const { verificationCode, requestVerification, status: emailVerificationStatus } = useEmailVerification();

    useEffect(() => {
        if (formStatus.submitting) {
            loginUser(email, password, member.id, orderType)
        }
    }, [formStatus.submitting]);

    useEffect(() => {
        if (formStatus.submitting && authenticationInfo?.authenticated) {
            if (authenticationInfo?.needsVerified) {
                requestVerification(authenticationInfo?.individualEmailId);
            } else {
                const redirectUrl = authenticationInfo.magicLink || `/u/auth/login?memberId=${member.id}`;
                window.location.assign(redirectUrl);
            }
        }
    }, [authenticationInfo]);

    useEffect(() => {
        if (formStatus.submitting && verificationCode?.sentEmail) {
            setFormStatus((prevState) => ({
                ...prevState,
                submitting: false
            }));
            browserHistory.push(`/u/registration/${member.id}/institution/confirm`);
        }
    }, [verificationCode]);

    useEffect(() => {
        const errorStatus = [authenticationStatus, emailVerificationStatus].find(status => status && status.hasError);
        if (formStatus.submitting && errorStatus) {
            setFormStatus({
                showErrorBanner: true,
                errorBannerMessage: errorStatus.errorMessage,
                submitting: false
            });
        }
    }, [authenticationStatus, emailVerificationStatus]);


    const handleSubmit = () => {
        setFormStatus((prevState) => ({
            ...prevState,
            submitting: true
        }));
    };

    const forgotPasswordUrl = `/u/auth/forgot?returnUrl=/u/registration/${member.id}/institution`;

    const buttonLabel = formStatus.submitting ? ( <Loader loading loadingMessage='Loading...' /> ): 'Continue';
    const buttonDisabled = formStatus.submitting || passwordValidationState !== VALIDATION_STATES.success;
    return (
        <FormContainer context="recessed" id="login-form-container">
            {
                formStatus.showErrorBanner && <FormContainer.Prefix id="error-banner-container">
                    <Banner visible={formStatus.showErrorBanner} title="error" context="error" dismissable={true}
                            size="large" message={formStatus.errorBannerMessage}
                            onDismiss={() => {
                                setFormStatus((prevState) => ({
                                    ...prevState,
                                    showErrorBanner: false,
                                    errorBannerMessage: ''
                                }));
                            }} />
                </FormContainer.Prefix>
            }

            <Heading4 withExtraSpace>Welcome Back! Sign In To Your Parchment Account</Heading4>
            <EmailSection>
                <span>
                    {email}
                    <ChangeEmail id="change-email" onClick={() => resetEmail(orderType)}>
                        <EditIcon />
                    </ChangeEmail>
                </span>
                <Anchor id="forgot-password-link" href={forgotPasswordUrl}>Forgot your password?</Anchor>
            </EmailSection>
            <Form className='password-form' onSubmit={() => handleSubmit()}>
                <Password
                    id="user-password"
                    context="authenticate"
                    required={true}
                    internalLabel={true}
                    value={password}
                    onChange={value => setPassword(value)}
                    onValidate={validationState => setPasswordValidationState(validationState)}
                />
                <ContinueButton id="continue-login-button" type='submit' context="primary"
                                name="continue-login-button" block
                                disabled={buttonDisabled}>{buttonLabel}</ContinueButton>
            </Form>
            <FormNoteMessage>
                <RequiredIcon color={thunderBird} />
                <p>All items marked with a red asterisk are required</p>
            </FormNoteMessage>
        </FormContainer>
    );
};

const EmailSection = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 16px;
    font-weight: 600;
    margin: 15px 0;
    flex-wrap: wrap;
`;
EmailSection.displayName = 'EmailSection';

const Heading4 = styled(Heading4Raw)`
    font-size: 18px;
    font-weight: 600;
    text-transform: uppercase;
`;
Heading4.displayName = 'Heading4';

const ChangeEmail = styled(Anchor)`
    margin-left: 10px;
`;
ChangeEmail.displayName = 'ChangeEmail';

const ContinueButton = styled(Button)`
    margin: 20px 0;
`;
ContinueButton.displayName = 'ContinueButton';

const RequiredIcon = styled(ValidationRequiredIcon)`
    height: 15px;
    width: 15px;
    border: 1px solid ${alto};
    background-color: ${white};
`;
RequiredIcon.displayName = 'RequiredIcon';

const FormNoteMessage = styled.div`
    display: flex;
    justify-content: center;

    p {
        font-size: 14px;
        font-weight: 600;
        margin: 0 0 0 15px;
    }
`;
FormNoteMessage.displayName = 'FormNoteMessage';
